// Bakes 3D text and imported models into triangle soups for SceneDesc.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { loadFont } from '../depth/fonts';
import { buildTextGeometry, type TextParams } from '../depth/text3d';
import { f32ToBase64, newId, type MeshData } from './types';

const MAX_TRIANGLES = 400_000;

/** Non-indexed positions of all meshes under `root`, in root space. */
function collectTriangles(root: THREE.Object3D): Float32Array {
  root.updateMatrixWorld(true);
  const parts: Float32Array[] = [];
  let total = 0;
  const v = new THREE.Vector3();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry?.attributes.position) return;
    const g = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const pos = g.attributes.position;
    const out = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      out[i * 3] = v.x;
      out[i * 3 + 1] = v.y;
      out[i * 3 + 2] = v.z;
    }
    parts.push(out);
    total += out.length;
  });
  if (total / 9 > MAX_TRIANGLES) throw new Error(`model has ${Math.round(total / 9)} triangles (max ${MAX_TRIANGLES})`);
  const all = new Float32Array(total);
  let off = 0;
  for (const p of parts) {
    all.set(p, off);
    off += p.length;
  }
  return all;
}

/** Centres the triangles and scales them to fit a `size`-sized cube. */
function normalize(t: Float32Array, size: number): Float32Array {
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  for (let i = 0; i < t.length; i += 3) box.expandByPoint(v.set(t[i], t[i + 1], t[i + 2]));
  const c = box.getCenter(new THREE.Vector3());
  const s = box.getSize(new THREE.Vector3());
  const k = size / Math.max(s.x, s.y, s.z, 1e-6);
  for (let i = 0; i < t.length; i += 3) {
    t[i] = (t[i] - c.x) * k;
    t[i + 1] = (t[i + 1] - c.y) * k;
    t[i + 2] = (t[i + 2] - c.z) * k;
  }
  return t;
}

export function meshData(t: Float32Array): MeshData {
  return { id: newId('m'), positions: f32ToBase64(t) };
}

/** 3D text as a mesh about 1 unit tall per line (centred). */
export async function bakeText(p: TextParams): Promise<MeshData> {
  const font = await loadFont(p.font);
  const g = buildTextGeometry(font, p);
  const mesh = new THREE.Mesh(g);
  // Font size 100 -> 1 world unit.
  mesh.scale.setScalar(0.01);
  const t = collectTriangles(mesh);
  g.dispose();
  return meshData(t);
}

export async function bakeModel(file: File, size = 3): Promise<MeshData> {
  const name = file.name.toLowerCase();
  const buf = await file.arrayBuffer();
  let root: THREE.Object3D;
  if (name.endsWith('.stl')) {
    root = new THREE.Mesh(new STLLoader().parse(buf));
  } else if (name.endsWith('.obj')) {
    root = new OBJLoader().parse(new TextDecoder().decode(buf));
  } else if (name.endsWith('.glb') || name.endsWith('.gltf')) {
    const gltf = await new GLTFLoader().parseAsync(buf, '');
    root = gltf.scene;
  } else {
    throw new Error('supported formats: glTF/GLB, STL, OBJ');
  }
  const t = collectTriangles(root);
  if (!t.length) throw new Error('the file contains no triangles');
  return meshData(normalize(t, size));
}
